// ============================================
// Cache Manager for Dashboard Data
// ============================================

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class CacheManager {
  private memoryCache: Map<string, CacheEntry<any>> = new Map();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes default
  private readonly USE_LOCAL_STORAGE = true;  // Enable localStorage persistence
  
  /**
   * Get data from cache
   * @param key - Cache key
   * @returns Cached data or null if expired/not found
   */
  get<T>(key: string): T | null {
    const memoryEntry = this.memoryCache.get(key);
    if (memoryEntry && this.isValid(memoryEntry)) {
      return memoryEntry.data;
    }

    if (this.USE_LOCAL_STORAGE) {
      try {
        const stored = localStorage.getItem(`cache_${key}`);
        if (stored) {
          const entry: CacheEntry<T> = JSON.parse(stored);
          if (this.isValid(entry)) {
            this.memoryCache.set(key, entry);
            return entry.data;
          } else {
            localStorage.removeItem(`cache_${key}`);
          }
        }
      } catch (error) {
        console.error(`Failed to retrieve from localStorage: ${key}`, error);
      }
    }
    return null;
  }
  
  /**
   * Set data in cache
   * @param key - Cache key
   * @param data - Data to cache
   * @param ttl - Time to live in milliseconds (optional)
   */
  set<T>(key: string, data: T, ttl?: number): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.DEFAULT_TTL,
    };

    this.memoryCache.set(key, entry);

    if (this.USE_LOCAL_STORAGE) {
      try {
        localStorage.setItem(`cache_${key}`, JSON.stringify(entry));
      } catch (error) {
        console.error(`Failed to store in localStorage: ${key}`, error);
        this.clearExpiredLocalStorage();
      }
    }
  }
  
  /**
   * Check if cache entry is still valid
   */
  private isValid<T>(entry: CacheEntry<T>): boolean {
    return Date.now() - entry.timestamp < entry.ttl;
  }
  
  /**
   * Clear specific cache entry
   */
  clear(key: string): void {
    this.memoryCache.delete(key);
    if (this.USE_LOCAL_STORAGE) {
      localStorage.removeItem(`cache_${key}`);
    }
  }
  
  /**
   * Clear all cache entries
   */
  clearAll(): void {
    this.memoryCache.clear();
    if (this.USE_LOCAL_STORAGE) {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('cache_')) {
          localStorage.removeItem(key);
        }
      });
    }
  }
  
  /**
   * Clear expired entries from localStorage
   */
  private clearExpiredLocalStorage(): void {
    if (!this.USE_LOCAL_STORAGE) return;
    
    const keys = Object.keys(localStorage);
    
    keys.forEach(key => {
      if (key.startsWith('cache_')) {
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            const entry = JSON.parse(stored);
            if (!this.isValid(entry)) {
              localStorage.removeItem(key);
            }
          }
        } catch (error) {
          localStorage.removeItem(key);
        }
      }
    });
  }
  
  /**
   * Get cache statistics
   */
  getStats(): {
    memoryEntries: number;
    localStorageEntries: number;
    totalSize: string;
  } {
    const memoryEntries = this.memoryCache.size;
    let localStorageEntries = 0;
    let totalSize = 0;
    
    if (this.USE_LOCAL_STORAGE) {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('cache_')) {
          localStorageEntries++;
          const item = localStorage.getItem(key);
          if (item) {
            totalSize += item.length;
          }
        }
      });
    }
    
    return {
      memoryEntries,
      localStorageEntries,
      totalSize: `${(totalSize / 1024).toFixed(2)} KB`,
    };
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();

// Export cache keys as constants to avoid typos
export const CACHE_KEYS = {
  MENTORSHIP_STATS: 'mentorship_stats',
  SWIPE_STATS: 'swipe_stats',
  PROFILE_STATS: 'profile_stats',
  ALL_DASHBOARD_STATS: 'all_dashboard_stats',
  // User profile caching keys
  USER_PROFILE: 'user_profile', // Individual user profile: user_profile_{uid}
  TENANT_USERS: 'tenant_users', // All users for a tenant: tenant_users_{tenantId}
  USER_PRIVATE_DATA: 'user_private_data', // Private user data: user_private_data_{uid}
  // Avatar image URL caching
  AVATAR_URL: 'avatar_url', // Firebase Storage avatar URL: avatar_url_{uid}
} as const;

// Export TTL presets
export const CACHE_TTL = {
  SHORT: 1 * 60 * 1000,      // 1 minute
  MEDIUM: 5 * 60 * 1000,      // 5 minutes
  LONG: 15 * 60 * 1000,       // 15 minutes
  VERY_LONG: 60 * 60 * 1000,  // 1 hour
} as const;